/**
 * depict-variant-fix.js
 * ---------------------------------------------------------------------------
 * Workaround for two defects in the Depict "duplicate colour variants" feature
 * on the Release theme (Digifist).
 *
 *  1. Depict clones a <product-card> and rewrites input[id] / label[for], but
 *     NOT input[name]. Radio inputs share a group by name, and these radios are
 *     not inside a <form>, so every duplicate of a product ends up in ONE
 *     document-wide radio group. Only one radio per group can be checked, so
 *     all but one card lose their selection entirely - swatches appear dead,
 *     sizes get marked unavailable, and no image/price/link update fires.
 *
 *  2. Depict repaints the clone by swapping image URLs in place rather than
 *     re-rendering the card, which inverts the theme's [data-product-images]
 *     variant -> image map. It also leaves the checked attribute on the
 *     original colour and leaves the product link without ?variant=.
 *
 * This script re-scopes each card's radio names, rebuilds each card's variant
 * images from the theme's own variant JSON (which Depict does not touch), and
 * selects the colour the duplicate is actually meant to represent.
 *
 * Safe to remove once Depict fixes the clone routine.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var SCOPED = 'variantScopeApplied';   // dataset flag - names already scoped
  var SYNCED = 'variantScopeSynced';    // dataset flag - last image synced
  var uid = 0;

  /* ---------------------------------------------------------------- helpers */

  function baseName(url) {
    if (!url) return '';
    return String(url).split('?')[0].split('#')[0].split('/').pop().toLowerCase();
  }

  function absolute(url) {
    try {
      return new URL(url, window.location.origin).toString();
    } catch (e) {
      return url;
    }
  }

  function withWidth(url, width) {
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function variantMediaSrc(variant) {
    if (!variant) return '';
    if (variant.featured_media && variant.featured_media.preview_image) {
      return variant.featured_media.preview_image.src;
    }
    if (variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    return '';
  }

  function getVariants(card) {
    var node = card.querySelector('[data-product-variants-json]');
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* ------------------------------------------------ 1. scope the radio names */

  function scopeNames(card) {
    if (card.dataset[SCOPED]) return;

    var radios = card.querySelectorAll('input[type="radio"][name]');
    if (!radios.length) {
      card.dataset[SCOPED] = 'none';
      return;
    }

    var token = '--dvf' + (++uid);
    card.dataset[SCOPED] = token;

    for (var i = 0; i < radios.length; i++) {
      if (radios[i].name.indexOf('--dvf') === -1) radios[i].name += token;
    }

    // The browser has already collapsed the shared group, so restore a valid
    // selection inside this card from the server-rendered checked attribute.
    var groups = card.querySelectorAll('.js-product-card-options');
    for (var g = 0; g < groups.length; g++) {
      var target = groups[g].querySelector('input[type="radio"][checked]') ||
                   groups[g].querySelector('input[type="radio"]');
      if (!target) continue;
      target.checked = true;
      target.removeAttribute('disabled');
      var li = target.closest('li');
      if (li) li.classList.remove('disabled');
    }

    resync(card);
  }

  /* --------------------------------- 2. repair images + select right colour */

  function syncDuplicate(card) {
    var anchor = card.querySelector('[data-depict-default-image]');
    if (!anchor) return;

    var wanted = baseName(anchor.getAttribute('data-depict-default-image'));
    if (!wanted) return;
    if (card.dataset[SYNCED] === wanted) return;   // already handled this value
    card.dataset[SYNCED] = wanted;

    var variants = getVariants(card);
    if (!variants.length) return;

    // -- rebuild every [data-product-images] element from the variant JSON.
    // Depict swapped these URLs around; the JSON is untouched and authoritative.
    var media = card.querySelectorAll('[data-product-images]');
    var target = null;

    for (var v = 0; v < variants.length; v++) {
      if (baseName(variantMediaSrc(variants[v])) === wanted) { target = variants[v]; break; }
    }

    for (var m = 0; m < media.length; m++) {
      var el = media[m];
      var ids = (el.getAttribute('data-product-images') || '').split(',');
      var firstId = ids[0];
      var match = null;

      for (var k = 0; k < variants.length; k++) {
        if (String(variants[k].id) === String(firstId)) { match = variants[k]; break; }
      }

      // The element keyed to the product id is the card's resting image - it
      // should show whichever colour this duplicate represents.
      var src = variantMediaSrc(match || target);
      if (src) repaintImage(el, src);
    }

    if (!target) return;

    // -- work out which option axis is the colour, then select it.
    var sameMedia = variants.filter(function (x) {
      return baseName(variantMediaSrc(x)) === wanted;
    });

    var colourIndex = 0;
    for (var i = 1; i <= 3; i++) {
      var all = new Set(variants.map(function (x) { return x['option' + i]; }).filter(Boolean));
      var within = new Set(sameMedia.map(function (x) { return x['option' + i]; }).filter(Boolean));
      if (within.size === 1 && all.size > 1) { colourIndex = i; break; }
    }
    if (!colourIndex) return;

    var groups = card.querySelectorAll('.js-product-card-options');
    var group = groups[colourIndex - 1];
    if (!group) return;

    var value = target['option' + colourIndex];
    var inputs = group.querySelectorAll('input[type="radio"]');
    var picked = null;

    for (var n = 0; n < inputs.length; n++) {
      if (inputs[n].value === value) { picked = inputs[n]; }
      inputs[n].removeAttribute('checked');
    }
    if (!picked) return;

    picked.checked = true;
    picked.setAttribute('checked', '');

    resync(card, true);
  }

  function repaintImage(img, mediaSrc) {
    var current = img.getAttribute('src') || '';
    if (baseName(current) === baseName(mediaSrc)) return;

    var width = null;
    try { width = new URL(current, window.location.origin).searchParams.get('width'); } catch (e) {}
    img.setAttribute('src', withWidth(absolute(mediaSrc), width || img.getAttribute('width') || 400));

    var srcset = img.getAttribute('srcset');
    if (!srcset) return;

    var parts = srcset.split(',');
    var rebuilt = [];
    for (var i = 0; i < parts.length; i++) {
      var bits = parts[i].trim().split(/\s+/);
      var descriptor = bits[bits.length - 1];
      if (!/^\d+w$/.test(descriptor)) { rebuilt = null; break; }   // bail on x-descriptors
      rebuilt.push(withWidth(absolute(mediaSrc), parseInt(descriptor, 10)) + ' ' + descriptor);
    }

    if (rebuilt) img.setAttribute('srcset', rebuilt.join(', '));
    else img.removeAttribute('srcset');
  }

  /* ------------------------------------------ 3. hand state back to the theme */

  function resync(card, updateMedia) {
    try {
      if (typeof card.updateVariantStatuses === 'function') card.updateVariantStatuses();
      if (typeof card.updateFormVariantIdInput === 'function') card.updateFormVariantIdInput();
      if (typeof card.setCartButtonState === 'function') card.setCartButtonState();

      if (typeof card.findCurrentVariantFromOptionRadioInputs !== 'function') return;
      var state = card.findCurrentVariantFromOptionRadioInputs();
      if (!state || !state.currentVariantId) return;

      if (updateMedia && typeof card.updateMediaForVariant === 'function') {
        card.updateMediaForVariant(state.currentVariantId);
      }

      // Point the tile at the colour it is showing, so the PDP opens on it.
      var links = card.querySelectorAll('[data-product-link]');
      for (var i = 0; i < links.length; i++) {
        var href = links[i].getAttribute('href');
        if (!href) continue;
        links[i].setAttribute('href', href.split('?variant=')[0] + '?variant=' + state.currentVariantId);
      }
    } catch (e) {
      /* never let this break the grid */
    }
  }

  /* -------------------------------------------------------------- 4. wiring */

  function process(card) {
    scopeNames(card);
    syncDuplicate(card);
  }

  function sweep(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches('product-card')) process(root);
    if (!root.querySelectorAll) return;
    var cards = root.querySelectorAll('product-card');
    for (var i = 0; i < cards.length; i++) process(cards[i]);
  }

  var queued = false;
  function schedule(nodes) {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      for (var i = 0; i < nodes.length; i++) sweep(nodes[i]);
    });
  }

  function start() {
    sweep(document.body);

    new MutationObserver(function (mutations) {
      var touched = [];
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === 'attributes') {
          var card = mutation.target.closest && mutation.target.closest('product-card');
          if (card) touched.push(card);
        } else {
          for (var j = 0; j < mutation.addedNodes.length; j++) touched.push(mutation.addedNodes[j]);
        }
      }
      if (touched.length) schedule(touched);
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-depict-default-image']   // deliberately narrow: our
                                                       // own src writes must not
                                                       // re-trigger the observer
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
